import styled from 'styled-components';
import { SkeletonLine } from './skeletonLine';

/** Matches the 210px height of .recent-graph-container / .weekly-graph-container so
 * swapping in the real chart does not change the card's height. */
const ChartArea = styled.div`
  height: 210px;
  padding-bottom: 14px;
  display: flex;
  flex-direction: column;
`;

const Plot = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  min-height: 0;
`;

const BarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  height: 100%;
  /* Seven columns at their label's natural width outgrow the narrowest task pane, so
     they are allowed to shrink and the label caps itself against the column. */
  min-width: 0;
`;

/** Holds the bar so its percentage height is measured against the plot alone, leaving
 * the label pill below it out of the calculation. */
const BarTrack = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 0;
`;

/** Stands in for the <Divider className="mb-8" /> below the real chart, matching its
 * 32px bottom margin so the card keeps the same height when the chart swaps in. */
const AxisRule = styled.div`
  height: 1px;
  background-color: var(--clr-border-grey-100);
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
  min-height: 18px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/** Percentages of the plot height, cycled across the bars — reproduces the design's
 * uneven silhouette without a random height that would flicker on every render. */
const BAR_HEIGHT_PATTERN = [98, 32, 63, 61, 100, 80, 63];

interface SkeletonChartProps {
  bars?: number;
  /** One per series in the real legend: billable hours alone, or actual plus budgeted. */
  legendItems?: number;
}

const buildBarKey = (index: number) => `skeleton-chart-bar-${index}`;
const buildLegendKey = (index: number) => `skeleton-chart-legend-${index}`;

const SkeletonChart = ({ bars = 7, legendItems = 1 }: SkeletonChartProps) => (
  <div data-testid="skeleton-chart">
    <ChartArea>
      <Plot>
        {Array.from({ length: bars }, (_, index) => (
          <BarColumn key={buildBarKey(index)} data-testid="skeleton-chart-bar">
            <BarTrack>
              <SkeletonLine
                $width="8px"
                $height={`${BAR_HEIGHT_PATTERN[index % BAR_HEIGHT_PATTERN.length]}%`}
                $radius="8px"
              />
            </BarTrack>
            <SkeletonLine $width="26px" $height="8px" $radius="4px" />
          </BarColumn>
        ))}
      </Plot>
    </ChartArea>
    <AxisRule />
    <Legend>
      {Array.from({ length: legendItems }, (_, index) => (
        <LegendItem key={buildLegendKey(index)} data-testid="skeleton-chart-legend-item">
          <SkeletonLine $width="14px" $height="6px" $radius="10px" />
          <SkeletonLine $width="56px" $height="8px" $radius="4px" />
          <SkeletonLine $width="30px" $height="8px" $radius="4px" />
        </LegendItem>
      ))}
    </Legend>
  </div>
);

export default SkeletonChart;
